import {
  Children,
  cloneElement,
  forwardRef,
  isValidElement,
  type ReactNode,
  type Ref,
} from 'react';

import { cn } from '#/lib/utils';

import { Overline } from './Overline';

/**
 * Headline variants -> desktop font sizes
 * 1. 82
 * 2. 64
 * 3. 50
 * 4. 42
 * 5. 34
 * 6. 28
 */

type Props = {
  value: string;
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'span';
  overline?: string | null;
  /** Extra classes for the overline, e.g. a theme color. */
  overlineClassName?: string;
  className?: string;
  dataSanity?: string;
  variant?: 1 | 2 | 3 | 4 | 5 | 6;
  /** Class for text wrapped in curly brackets. Defaults to purple for light backgrounds. */
  highlightClass?: string;
};

const LIGHT_BACKGROUND_HIGHLIGHT_CLASS = 'text-cipher';
const DARK_BACKGROUND_HIGHLIGHT_CLASS = 'text-electric';

const getHexLuminance = (hex: string) => {
  const normalizedHex = hex.replace('#', '');

  if (![3, 6].includes(normalizedHex.length)) return null;

  const channels =
    normalizedHex.length === 3
      ? normalizedHex.split('').map((value) => value + value)
      : [
          normalizedHex.slice(0, 2),
          normalizedHex.slice(2, 4),
          normalizedHex.slice(4, 6),
        ];

  const [r = 0, g = 0, b = 0] = channels.map((channel) => {
    const value = parseInt(channel, 16) / 255;
    return value <= 0.03928
      ? value / 12.92
      : Math.pow((value + 0.055) / 1.055, 2.4);
  });

  return r * 0.2126 + g * 0.7152 + b * 0.0722;
};

const DARK_BACKGROUND_VALUES = new Set([
  'dark',
  'black',
  'charcoal',
  'grayscale-06',
  'cipher-08',
]);

const LIGHT_FOREGROUND_VALUES = new Set([
  '#fff',
  '#ffffff',
  'white',
  'cloud',
  'light',
]);

export const getHeadlineHighlightClass = ({
  background,
  foreground,
}: {
  background?: string | null;
  foreground?: string | null;
}) => {
  const normalizedBackground = background?.toLowerCase();
  const normalizedForeground = foreground?.toLowerCase();

  if (
    normalizedForeground &&
    LIGHT_FOREGROUND_VALUES.has(normalizedForeground)
  ) {
    return DARK_BACKGROUND_HIGHLIGHT_CLASS;
  }

  if (
    normalizedBackground &&
    DARK_BACKGROUND_VALUES.has(normalizedBackground)
  ) {
    return DARK_BACKGROUND_HIGHLIGHT_CLASS;
  }

  const luminance = background ? getHexLuminance(background) : null;

  return luminance !== null && luminance < 0.4
    ? DARK_BACKGROUND_HIGHLIGHT_CLASS
    : undefined;
};

export const formatHeadline = (
  value: string,
  highlightClass = LIGHT_BACKGROUND_HIGHLIGHT_CLASS,
) => {
  const segments = [];
  const parts = value.split(/(\{|\})/);
  let isCurlyText = false;

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    if (part === '{') {
      isCurlyText = true;
    } else if (part === '}') {
      isCurlyText = false;
    } else if (isCurlyText) {
      // Wrap text inside curly brackets with a span and the desired class
      segments.push(
        <span key={i} className={highlightClass}>
          {part}
        </span>,
      );
    } else {
      // Push regular text directly
      segments.push(part);
    }
  }
  return segments;
};

/** Apply `{highlight}` formatting to string leaves inside Portable Text children. */
export const highlightTextNodes = (
  children: ReactNode,
  highlightClass = LIGHT_BACKGROUND_HIGHLIGHT_CLASS,
): ReactNode =>
  Children.map(children, (child) => {
    if (typeof child === 'string') {
      return formatHeadline(child, highlightClass);
    }
    if (
      isValidElement<{ children?: ReactNode }>(child) &&
      child.props.children != null
    ) {
      return cloneElement(child, {
        children: highlightTextNodes(child.props.children, highlightClass),
      });
    }
    return child;
  });

export const Headline = forwardRef(
  (
    {
      value,
      as: Tag = 'h2',
      overline,
      overlineClassName,
      className,
      dataSanity,
      variant = 4,
      highlightClass,
    }: Props,
    ref: Ref<HTMLHeadingElement>,
  ) => {
    // Function to split the value into an array of strings and JSX elements

    // Process the value and store the segments
    const content = formatHeadline(value, highlightClass);

    return (
      <Tag
        ref={ref}
        data-sanity={dataSanity}
        className={cn(
          'text-balance font-[440] leading-105 -tracking-1',
          {
            'text-42 m:text-70 l:text-82': variant === 1,
            'text-36 m:text-56 l:text-64': variant === 2,
            'text-32 m:text-43 l:text-50': variant === 3,
            'text-28 m:text-35 l:text-45': variant === 4,
            'text-26 m:text-30 l:text-34': variant === 5,
            'text-22 m:text-25 l:text-28': variant === 6,
          },
          className,
        )}
      >
        {overline ? (
          <Overline
            className={cn('mb-15 l:mb-22', overlineClassName)}
            as="span"
          >
            {overline}
          </Overline>
        ) : null}
        {content}
      </Tag>
    );
  },
);

Headline.displayName = 'Headline';
